import { useState, useCallback } from 'react';
import { useMtlsOrganizations } from '../../hooks/useMtlsData';
import { useFilterState } from '../../hooks/useFilterState';
import { ApiService } from '../../services/api';
import { 
  SearchFilter, 
  ConfirmationModal, 
  StatusBadge 
} from './shared';
import { AdvancedFilter } from './shared/AdvancedFilter';
import { UniversalSchemaForm } from '../shared/UniversalSchemaForm';
import { UniversalSchemaTable } from '../shared/UniversalSchemaTable';
import { MTLS_OPENAPI } from '../../schemas';
import { 
  handleApiError, 
  handleApiSuccess 
} from '../../utils/mtls-error-handler';
import { filterCreateOrganizationRequest, filterUpdateOrganizationRequest } from '../../utils/api-helpers';
import type { components } from '../../types/api-generated';
import { formatDateTime } from '../../utils/date-utils';
import { 
  OrganizationFilters, 
  MtlsOrganization 
} from '../../types/mtls';
import { OrganizationDetailPanel } from './OrganizationDetailPanel';

// Use OpenAPI generated types
type Certificate = components['schemas']['Certificate'];
type Product = components['schemas']['Product'];

export function OrganizationManagement(): JSX.Element {
  const {
    filters,
    updateFilters,
    resetFilters,
  } = useFilterState({
    initialFilters: { page: 1, size: 10 },
    persistKey: 'organizations',
  });
  
  const { organizations, loading, error, reload } = useMtlsOrganizations(filters as OrganizationFilters);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCertificatesModal, setShowCertificatesModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<MtlsOrganization | null>(null);
  const [organizationCertificates, setOrganizationCertificates] = useState<Certificate[]>([]);
  const [organizationProducts, setOrganizationProducts] = useState<Product[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    updateFilters({ [key]: value, page: 1 });
  }, [updateFilters]);

  const handleAdvancedFilterChange = useCallback((key: string, value: any) => {
    updateFilters({ [key]: value, page: 1 });
  }, [updateFilters]);

  const handleCreate = () => {
    setShowCreateModal(true);
  };

  const handleEdit = (organization: MtlsOrganization) => {
    setSelectedOrganization(organization);
    setShowEditModal(true);
  };

  const handleDelete = (organization: MtlsOrganization) => {
    setSelectedOrganization(organization);
    setShowDeleteModal(true);
  };

  const handleViewCertificates = async (organization: MtlsOrganization) => {
    setSelectedOrganization(organization);
    try {
      const response = await ApiService.listMtlsOrganizationCertificates(organization.id);
      setOrganizationCertificates(response.certificates || []);
      setShowCertificatesModal(true);
    } catch (error) {
      handleApiError(error, 'Certificate Lookup');
    }
  };

  const handleViewProducts = async (organization: MtlsOrganization) => {
    setSelectedOrganization(organization);
    try {
      const response = await ApiService.listMtlsOrganizationProducts(organization.id);
      setOrganizationProducts(response.products || []);
      setShowProductsModal(true);
    } catch (error) {
      handleApiError(error, 'Product Lookup');
    }
  };

  const handleViewDetails = (organization: MtlsOrganization) => {
    setSelectedOrganization(organization);
    setShowDetailPanel(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedOrganization) return;

    setSubmitting(true);
    try {
      await ApiService.deleteMtlsOrganization(selectedOrganization.id);
      handleApiSuccess('Organization deleted successfully', 'Organization Management');
      setShowDeleteModal(false);
      reload();
    } catch (error) {
      handleApiError(error, 'Organization Management');
    } finally {
      setSubmitting(false);
    }
  };

  const filterOptions = [
    {
      key: 'status',
      label: 'Filter by Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ];

  const advancedFilterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'active', label: 'Active', count: organizations.filter(o => o.status === 'active').length },
        { value: 'inactive', label: 'Inactive', count: organizations.filter(o => o.status === 'inactive').length },
      ],
    },
    {
      key: 'hasProducts',
      label: 'Has Products',
      type: 'boolean' as const,
      placeholder: 'Filter by product presence',
    },
    {
      key: 'hasCertificates',
      label: 'Has Certificates',
      type: 'boolean' as const,
      placeholder: 'Filter by certificate presence',
    },
    {
      key: 'createdDateRange',
      label: 'Created Date Range',
      type: 'daterange' as const,
    },
    {
      key: 'productCount',
      label: 'Minimum Product Count',
      type: 'number' as const,
      min: 0,
      placeholder: 'Minimum number of products',
    },
    {
      key: 'certificateCount',
      label: 'Minimum Certificate Count',
      type: 'number' as const,
      min: 0,
      placeholder: 'Minimum number of certificates',
    },
    {
      key: 'contactInfo',
      label: 'Contact Information',
      type: 'multiselect' as const,
      options: [
        { value: 'hasEmail', label: 'Has Email' },
        { value: 'hasPhone', label: 'Has Phone' },
        { value: 'hasNotificationEmail', label: 'Has Notification Email' },
        { value: 'hasNotificationContact', label: 'Has Notification Contact' },
      ],
    },
  ];

  const renderFormModal = (isEdit: boolean) => {
    const title = isEdit ? 'Edit Organization' : 'Create Organization';
    const isOpen = isEdit ? showEditModal : showCreateModal;
    const onClose = () => isEdit ? setShowEditModal(false) : setShowCreateModal(false);

    if (!isOpen) return null;

    // Prepare initial data for edit mode
    const initialData = isEdit && selectedOrganization ? {
      name: selectedOrganization.name || '',
      email: selectedOrganization.email || '',
      contactNumber: selectedOrganization.contactNumber || '',
      notificationEmail: selectedOrganization.notificationEmail || '',
      notificationContactName: selectedOrganization.notificationContactName || '',
    } : {};

    const handleSchemaFormSubmit = async (data: Record<string, any>) => {
      setSubmitting(true);
      try {
        if (isEdit && selectedOrganization) {
          // Filter out empty optional fields for update request
          const filteredData = filterUpdateOrganizationRequest(data as components['schemas']['UpdateOrganizationRequest']);
          await ApiService.updateMtlsOrganization(selectedOrganization.id, filteredData);
          handleApiSuccess('Organization updated successfully', 'Organization Management');
          setShowEditModal(false);
        } else {
          // Filter out empty optional fields for create request
          const filteredData = filterCreateOrganizationRequest(data as components['schemas']['CreateOrganizationRequest']);
          await ApiService.createMtlsOrganization(filteredData);
          handleApiSuccess('Organization created successfully', 'Organization Management');
          setShowCreateModal(false);
        }
        
        reload();
      } catch (error) {
        handleApiError(error, 'Organization Management');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <>
        <div className="modal-backdrop fade show"></div>
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">{title}</h4>
                <button
                  type="button"
                  className="close"
                  onClick={onClose}
                  disabled={submitting}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <UniversalSchemaForm
                  openApiSpec={MTLS_OPENAPI}
                  schemaName={isEdit ? 'UpdateOrganizationRequest' : 'CreateOrganizationRequest'}
                  onSubmit={handleSchemaFormSubmit}
                  initialData={initialData}
                  loading={submitting}
                  showSubmitButton={false}
                  formId={`organization-form-${isEdit ? 'edit' : 'create'}`}
                  customLabels={{
                    name: 'Organization Name',
                    email: 'Email Address',
                    contactNumber: 'Phone Number',
                    notificationEmail: 'Notification Email',
                    notificationContactName: 'Notification Contact Name'
                  }}
                  customPlaceholders={{
                    name: 'e.g., Rail Delivery Group',
                    email: 'e.g., contact@example.com',
                    contactNumber: 'e.g., +44 20 1234 5678',
                    notificationEmail: 'e.g., notifications@example.com',
                    notificationContactName: 'e.g., John Smith'
                  }}
                  customPatterns={{
                    email: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
                    notificationEmail: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
                    contactNumber: '^[\\+]?[0-9\\s\\-\\(\\)]{7,20}$'
                  }}
                />
              </div>
              <div className="modal-footer justify-content-between">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form={`organization-form-${isEdit ? 'edit' : 'create'}`}
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting && (
                    <span className="spinner-border spinner-border-sm mr-2" role="status">
                      <span className="sr-only">Loading...</span>
                    </span>
                  )}
                  <i className="fas fa-save mr-2"></i>
                  {isEdit ? 'Update' : 'Create'} Organization
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  if (error) {
    return (
      <div className="alert alert-danger">
        <h4><i className="icon fas fa-ban"></i> Error!</h4>
        Failed to load organizations: {error}
        <div className="mt-2">
          <button className="btn btn-outline-danger" onClick={reload}>
            <i className="fas fa-redo mr-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-management">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-sm-6">
          <h3>
            <i className="fas fa-building mr-2"></i>
            Organizations
          </h3>
        </div>
        <div className="col-sm-6 text-right">
          <button
            className="btn btn-primary"
            onClick={handleCreate}
          >
            <i className="fas fa-plus mr-2"></i>
            New Organization
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card card-outline card-primary">
        <div className="card-body">
          <SearchFilter
            searchPlaceholder="Search organizations by name, email, or contact information..."
            searchValue={filters.search}
            onSearchChange={handleSearch}
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            onClear={resetFilters}
          />
          
          <AdvancedFilter
            filters={advancedFilterConfig}
            values={filters}
            onChange={handleAdvancedFilterChange}
            onReset={resetFilters}
            className="mt-3"
          />
        </div>
      </div>

      {/* Organizations Table */}
      <div className="card card-outline card-primary">
        <div className="card-body">
          <UniversalSchemaTable
            openApiSpec={MTLS_OPENAPI}
            schemaName="OrganizationResponse"
            data={organizations}
            loading={loading}
            emptyMessage="No organizations found. Create your first organization to get started."
            onView={undefined} // We'll add a view modal later if needed
            onEdit={handleEdit}
            onDelete={handleDelete}
            customActions={[
              {
                label: 'View Details',
                icon: 'fas fa-eye',
                onClick: handleViewDetails
              },
              {
                label: 'View Certificates',
                icon: 'fas fa-certificate',
                onClick: handleViewCertificates
              },
              {
                label: 'View Products',
                icon: 'fas fa-cube',
                onClick: handleViewProducts
              }
            ]}
            hiddenFields={[
              'id', // Hide internal ID
              'createdBy', // Hide internal fields
              'updatedBy',
              'updatedAt'
            ]}
            customLabels={{
              name: 'Organization Name',
              email: 'Contact Email',
              contactNumber: 'Phone',
              notificationEmail: 'Notification Email',
              certificateCount: 'Certificates',
              productCount: 'Products',
              status: 'Status',
              createdAt: 'Created',
              description: 'Description'
            }}
            customColumnTypes={{
              status: 'badge',
              certificateCount: 'badge',
              productCount: 'badge',
              createdAt: 'datetime',
              updatedAt: 'datetime',
              email: 'email',
              notificationEmail: 'email',
              contactNumber: 'phone'
            }}
            striped={true}
            hover={true}
            responsive={true}
          />
        </div>
      </div>

      {/* Org Detail Panel */}
      {showDetailPanel && selectedOrganization && (
        <OrganizationDetailPanel
          organization={selectedOrganization}
          onClose={() => setShowDetailPanel(false)}
        />
      )}

      {/* Modals */}
      {renderFormModal(false)}
      {renderFormModal(true)}
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Organization"
        message={`Are you sure you want to delete "${selectedOrganization?.name}"? This action cannot be undone. If there are associated certificates, products, or assignments, you will receive instructions on how to clean them up first.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        loading={submitting}
      />

      {/* Organization Certificates Modal */}
      {showCertificatesModal && selectedOrganization && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-certificate mr-2"></i>
                    Certificates - {selectedOrganization.name}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowCertificatesModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {organizationCertificates.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="fas fa-certificate fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No certificates found for this organization.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Common Name</th>
                            <th>Environment</th>
                            <th>Certificate Type</th>
                            <th>Status</th>
                            <th>Expiry Date</th>
                            <th>Created By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organizationCertificates.map((cert) => (
                            <tr key={cert.certificateId}>
                              <td>
                                <strong>{cert.commonName}</strong>
                                {cert.serialNumber && (
                                  <div className="text-muted small">Serial: {cert.serialNumber}</div>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${cert.environment === 'prod' ? 'badge-danger' : 'badge-warning'}`}>
                                  {cert.environment}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${cert.certificateType === 'server' ? 'badge-primary' : 'badge-info'}`}>
                                  {cert.certificateType}
                                </span>
                              </td>
                              <td>
                                <StatusBadge status={cert.status || 'active'} type="certificate" />
                              </td>
                              <td>{cert.expiresAt ? formatDateTime(cert.expiresAt) : '-'}</td>
                              <td>
                                <span className="text-muted small">
                                  {cert.createdBy}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificatesModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Organization Products Modal */}
      {showProductsModal && selectedOrganization && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fas fa-cube mr-2"></i>
                    Products - {selectedOrganization.name}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowProductsModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {organizationProducts.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="fas fa-cube fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No products found for this organization.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Product Name</th>
                            <th>Description</th>
                            <th>Created</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {organizationProducts.map((product) => (
                            <tr key={product.id}>
                              <td>
                                <strong>{product.name}</strong>
                              </td>
                              <td>{product.description || '-'}</td>
                              <td>{product.createdAt ? formatDateTime(product.createdAt) : '-'}</td>
                              <td>{product.updatedAt ? formatDateTime(product.updatedAt) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowProductsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}